import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  Copy,
  Eye,
  EyeOff,
  Key,
  Package,
  Shield,
  User,
  Layers,
} from 'lucide-react';
import { Button, Card, CardTitle, Input, PageSpinner } from '@/components/ui';
import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import type { ResponseEnvelope } from '@/types/api';
import { isApiError } from '@/lib/api/envelope';
import { fullOnboardClient, type OnboardingResult } from './api';

// ── Product types ─────────────────────────────────────────────────────────────

interface ProductConfig {
  productCode: string;
  displayName: string;
  shortDescription: string;
  category: string;
  enabled: boolean;
  defaultDeductionAmount: number;
  billingUnit: string;
}

async function fetchAdminProducts(): Promise<ProductConfig[]> {
  const res = await api.get<ResponseEnvelope<ProductConfig[]>>(Endpoints.adminProducts.list);
  return unwrap(res);
}

// ── Schemas ───────────────────────────────────────────────────────────────────

const companySchema = z.object({
  companyName: z.string().min(2, 'Company name is required'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  contactPersonEmail: z.string().email('Valid email required'),
  contactPhoneNumber: z.string().optional(),
  expectedAuthVolume: z.string().optional(),
});

const packageSchema = z.object({
  packageName: z.string().min(1, 'Package name is required'),
  startingBalance: z.coerce
    .number({ invalid_type_error: 'Balance must be a number' })
    .positive('Balance must be positive')
    .int('Balance must be a whole number'),
  expiryDate: z.string().min(1, 'Expiry date is required'),
});

const userSchema = z.object({
  userRole: z.enum(
    [
      'AUTHMATECH_CLIENT_ADMIN',
      'AUTHMATECH_CLIENT_OPERATOR',
      'AUTHMATECH_CLIENT_FINANCE',
      'AUTHMATECH_CLIENT_VIEWER',
    ],
    { required_error: 'Please select a role' },
  ),
});

type CompanyValues = z.infer<typeof companySchema>;
type PackageValues = z.infer<typeof packageSchema>;
type UserValues = z.infer<typeof userSchema>;

// ── Steps ─────────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Company', icon: Building2 },
  { id: 2, label: 'Package', icon: Package },
  { id: 3, label: 'Products', icon: Layers },
  { id: 4, label: 'First User', icon: User },
  { id: 5, label: 'Review', icon: Shield },
];

// ── Role options ──────────────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  {
    value: 'AUTHMATECH_CLIENT_ADMIN',
    label: 'Client Admin',
    description: 'Full access: users, API keys, verifications, settings.',
  },
  {
    value: 'AUTHMATECH_CLIENT_OPERATOR',
    label: 'Client Operator',
    description: 'View verifications, trigger troubleshooting.',
  },
  {
    value: 'AUTHMATECH_CLIENT_FINANCE',
    label: 'Client Finance',
    description: 'View subscription, balance, and invoices.',
  },
  {
    value: 'AUTHMATECH_CLIENT_VIEWER',
    label: 'Client Viewer',
    description: 'Read-only workspace access.',
  },
];

// ── Product category labels ───────────────────────────────────────────────────

const CATEGORY_LABEL: Record<string, string> = {
  VERIFICATION: 'Verification',
  INTELLIGENCE: 'Intelligence',
  IDENTITY: 'Identity Recovery',
  CRM: 'CRM & Connect',
};

// ── Credentials reveal modal ──────────────────────────────────────────────────

function CredentialsModal({
  result,
  onDone,
}: {
  result: OnboardingResult;
  onDone: () => void;
}) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  function copyToClipboard(value: string, key: string) {
    void navigator.clipboard.writeText(value).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    });
  }

  function CopyButton({ value, id }: { value: string; id: string }) {
    const copied = copiedKey === id;
    return (
      <button
        type="button"
        onClick={() => copyToClipboard(value, id)}
        className="ml-2 rounded p-1 text-fg-muted hover:text-fg"
        title="Copy"
      >
        {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-surface shadow-2xl ring-1 ring-border">
        {/* Header */}
        <div className="border-b border-border px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/15">
              <Check className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <h2 className="text-base font-semibold">Client workspace created</h2>
              <p className="text-sm text-fg-muted">{result.companyName}</p>
            </div>
          </div>
        </div>

        {/* Status message */}
        <div className="mx-6 mt-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300">
          Client workspace has been created. The client has been emailed temporary access details
          and must complete activation from their workspace.
        </div>

        {/* Temp password warning */}
        <div className="mx-6 mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
          <strong>Copy the temporary password now.</strong> It will{' '}
          <strong>never be shown again</strong>.
        </div>

        {/* Credentials grid */}
        <div className="space-y-3 px-6 py-4">
          <CredentialRow label="Dashboard URL" value={result.dashboardUrl ?? '—'} id="url" showCopy>
            <CopyButton value={result.dashboardUrl ?? ''} id="url" />
          </CredentialRow>
          <CredentialRow label="Username (email)" value={result.username} id="email" showCopy>
            <CopyButton value={result.username} id="email" />
          </CredentialRow>
          <CredentialRow label="Client ID" value={result.clientId} id="clientId" showCopy>
            <CopyButton value={result.clientId} id="clientId" />
          </CredentialRow>

          {result.temporaryPassword && (
            <div className="flex items-center justify-between rounded-lg bg-surface-raised px-3 py-2">
              <div className="min-w-0">
                <p className="text-xs text-fg-muted">Temporary password</p>
                <p className="mt-0.5 font-mono text-sm break-all">
                  {showPassword ? result.temporaryPassword : '••••••••••••••••'}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="rounded p-1 text-fg-muted hover:text-fg"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <CopyButton value={result.temporaryPassword} id="password" />
              </div>
            </div>
          )}

          {result.keycloakNote && (
            <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-xs text-blue-700 dark:text-blue-300">
              <strong>Manual action required:</strong> {result.keycloakNote}
            </div>
          )}

          {result.enabledProducts && result.enabledProducts.length > 0 && (
            <div className="rounded-lg bg-surface-raised px-3 py-2">
              <p className="text-xs text-fg-muted">Enabled products</p>
              <p className="mt-0.5 text-sm">{result.enabledProducts.join(', ')}</p>
            </div>
          )}
        </div>

        <div className="border-t border-border px-6 py-4">
          <Button onClick={onDone} className="w-full">
            Done — go to client profile
          </Button>
        </div>
      </div>
    </div>
  );
}

function CredentialRow({
  label,
  value,
  id: _id,
  showCopy = false,
  children,
}: {
  label: string;
  value: string;
  id: string;
  showCopy?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-surface-raised px-3 py-2">
      <div className="min-w-0">
        <p className="text-xs text-fg-muted">{label}</p>
        <p className="mt-0.5 font-mono text-sm truncate">{value}</p>
      </div>
      {showCopy && children}
    </div>
  );
}

// ── Stepper indicator ─────────────────────────────────────────────────────────

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-0">
      {STEPS.map((step, i) => {
        const done = current > step.id;
        const active = current === step.id;
        const Icon = step.icon;
        return (
          <div key={step.id} className="flex items-center">
            <div className="flex flex-col items-center gap-1">
              <div
                className={[
                  'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors',
                  done
                    ? 'bg-primary text-white'
                    : active
                      ? 'bg-primary/15 text-primary ring-2 ring-primary'
                      : 'bg-surface-raised text-fg-muted',
                ].join(' ')}
              >
                {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
              </div>
              <span
                className={[
                  'text-xs',
                  active ? 'font-medium text-fg' : 'text-fg-muted',
                ].join(' ')}
              >
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={[
                  'mb-5 h-px w-8 mx-1',
                  done ? 'bg-primary' : 'bg-border',
                ].join(' ')}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Product selection step ────────────────────────────────────────────────────

function ProductSelectionStep({
  selected,
  onToggle,
  onNext,
}: {
  selected: string[];
  onToggle: (code: string) => void;
  onNext: () => void;
}) {
  const { data: products, isLoading } = useQuery({
    queryKey: ['adminProducts'],
    queryFn: fetchAdminProducts,
  });

  if (isLoading) return <PageSpinner />;

  const enabledProducts = (products ?? []).filter((p) => p.enabled);

  const byCategory = enabledProducts.reduce<Record<string, ProductConfig[]>>((acc, p) => {
    const cat = p.category ?? 'OTHER';
    if (!acc[cat]) acc[cat] = [];
    acc[cat]!.push(p);
    return acc;
  }, {});

  return (
    <Card>
      <CardTitle className="mb-1 flex items-center gap-2">
        <Layers className="h-4 w-4 text-primary" />
        Product entitlement
      </CardTitle>
      <p className="mb-4 text-sm text-fg-muted">
        Select the products to enable for this client. You can change these later from the client profile.
      </p>

      {Object.entries(byCategory).map(([category, catProducts]) => (
        <div key={category} className="mb-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-fg-muted">
            {CATEGORY_LABEL[category] ?? category}
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {catProducts.map((product) => {
              const isSelected = selected.includes(product.productCode);
              return (
                <button
                  key={product.productCode}
                  type="button"
                  onClick={() => onToggle(product.productCode)}
                  className={[
                    'flex items-start gap-3 rounded-xl border p-3 text-left transition-colors',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-border-hover',
                  ].join(' ')}
                >
                  <div
                    className={[
                      'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors',
                      isSelected
                        ? 'border-primary bg-primary text-white'
                        : 'border-border bg-surface',
                    ].join(' ')}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{product.displayName}</p>
                    <p className="text-xs text-fg-muted">{product.shortDescription}</p>
                    <p className="mt-0.5 text-xs text-fg-muted">
                      {product.defaultDeductionAmount} {product.billingUnit ?? 'credit'}/use
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {selected.length === 0 && (
        <p className="mb-2 text-sm text-amber-600 dark:text-amber-400">
          No products selected. You can enable products later from the client profile.
        </p>
      )}

      <div className="flex justify-end pt-2">
        <Button type="button" onClick={onNext}>
          Next: First User <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function ClientOnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [onboardResult, setOnboardResult] = useState<OnboardingResult | null>(null);

  const [companyData, setCompanyData] = useState<CompanyValues | null>(null);
  const [packageData, setPackageData] = useState<PackageValues | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [userData, setUserData] = useState<UserValues | null>(null);

  const companyForm = useForm<CompanyValues>({
    resolver: zodResolver(companySchema),
    defaultValues: companyData ?? {
      companyName: '',
      firstName: '',
      lastName: '',
      contactPersonEmail: '',
      contactPhoneNumber: '',
      expectedAuthVolume: '',
    },
  });

  const packageForm = useForm<PackageValues>({
    resolver: zodResolver(packageSchema),
    defaultValues: packageData ?? {
      packageName: 'Enterprise',
      startingBalance: 10000,
      expiryDate: '',
    },
  });

  const userForm = useForm<UserValues>({
    resolver: zodResolver(userSchema),
    defaultValues: userData ?? { userRole: 'AUTHMATECH_CLIENT_ADMIN' },
  });

  function toggleProduct(code: string) {
    setSelectedProducts((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  }

  const mutation = useMutation({
    mutationFn: () => {
      if (!companyData || !packageData || !userData) {
        throw new Error('Missing onboarding data');
      }
      return fullOnboardClient({
        companyName: companyData.companyName,
        firstName: companyData.firstName,
        lastName: companyData.lastName,
        contactPersonEmail: companyData.contactPersonEmail,
        contactPhoneNumber: companyData.contactPhoneNumber,
        expectedAuthVolume: companyData.expectedAuthVolume,
        packageName: packageData.packageName,
        startingBalance: packageData.startingBalance,
        expiryDate: new Date(packageData.expiryDate).toISOString(),
        userRole: userData.userRole,
        enabledProducts: selectedProducts,
      });
    },
    onSuccess: (result) => {
      setOnboardResult(result);
    },
  });

  const serverError =
    mutation.error && isApiError(mutation.error)
      ? mutation.error.firstErrorMessage
      : mutation.error
        ? 'An error occurred. Please try again.'
        : null;

  function handleBack() {
    if (step > 1) setStep((s) => s - 1);
    else navigate('/clients');
  }

  return (
    <>
      {onboardResult && (
        <CredentialsModal
          result={onboardResult}
          onDone={() => navigate(`/clients/${onboardResult.clientId}`)}
        />
      )}

      <div className="mx-auto max-w-2xl space-y-6">
        {/* Back */}
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="h-4 w-4" />
          {step > 1 ? 'Back' : 'Back to clients'}
        </button>

        <div>
          <h1 className="text-xl font-semibold">Onboard new client</h1>
          <p className="mt-0.5 text-sm text-fg-muted">
            Creates the client record, provisions dashboard access, and enables selected products.
            The client generates their API key after first sign-in.
          </p>
        </div>

        {/* Stepper */}
        <div className="flex justify-center">
          <StepIndicator current={step} />
        </div>

        {/* Step 1 — Company */}
        {step === 1 && (
          <Card>
            <CardTitle className="mb-4 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              Company information
            </CardTitle>
            <form
              onSubmit={companyForm.handleSubmit((values) => {
                setCompanyData(values);
                setStep(2);
              })}
              className="space-y-4"
            >
              <Input
                label="Company name"
                placeholder="Acme Corp"
                error={companyForm.formState.errors.companyName?.message}
                {...companyForm.register('companyName')}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="First name"
                  placeholder="Jane"
                  error={companyForm.formState.errors.firstName?.message}
                  {...companyForm.register('firstName')}
                />
                <Input
                  label="Last name"
                  placeholder="Smith"
                  error={companyForm.formState.errors.lastName?.message}
                  {...companyForm.register('lastName')}
                />
              </div>
              <Input
                label="Contact email"
                placeholder="jane@acme.com"
                error={companyForm.formState.errors.contactPersonEmail?.message}
                {...companyForm.register('contactPersonEmail')}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Phone number (optional)"
                  placeholder="+962791234567"
                  {...companyForm.register('contactPhoneNumber')}
                />
                <Input
                  label="Expected monthly volume (optional)"
                  placeholder="50000"
                  {...companyForm.register('expectedAuthVolume')}
                />
              </div>
              <div className="flex justify-end pt-2">
                <Button type="submit">
                  Next: Package <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Step 2 — Package */}
        {step === 2 && (
          <Card>
            <CardTitle className="mb-4 flex items-center gap-2">
              <Package className="h-4 w-4 text-primary" />
              Subscription & package
            </CardTitle>
            <form
              onSubmit={packageForm.handleSubmit((values) => {
                setPackageData(values);
                setStep(3);
              })}
              className="space-y-4"
            >
              <Input
                label="Package name"
                placeholder="Enterprise 50K"
                error={packageForm.formState.errors.packageName?.message}
                {...packageForm.register('packageName')}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="number"
                  label="Starting balance (verifications)"
                  placeholder="50000"
                  error={packageForm.formState.errors.startingBalance?.message}
                  {...packageForm.register('startingBalance')}
                />
                <Input
                  type="date"
                  label="Balance expiry date"
                  error={packageForm.formState.errors.expiryDate?.message}
                  {...packageForm.register('expiryDate')}
                />
              </div>
              <div className="flex justify-end pt-2">
                <Button type="submit">
                  Next: Products <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Step 3 — Product selection */}
        {step === 3 && (
          <ProductSelectionStep
            selected={selectedProducts}
            onToggle={toggleProduct}
            onNext={() => setStep(4)}
          />
        )}

        {/* Step 4 — First user / role */}
        {step === 4 && (
          <Card>
            <CardTitle className="mb-4 flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              First dashboard user
            </CardTitle>
            <p className="mb-4 text-sm text-fg-muted">
              The contact email <strong>{companyData?.contactPersonEmail}</strong> will be
              provisioned in Keycloak with a temporary password.
            </p>
            <form
              onSubmit={userForm.handleSubmit((values) => {
                setUserData(values);
                setStep(5);
              })}
              className="space-y-3"
            >
              {ROLE_OPTIONS.map((opt) => {
                const isSelected = userForm.watch('userRole') === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={[
                      'flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors',
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-border-hover',
                    ].join(' ')}
                  >
                    <input
                      type="radio"
                      value={opt.value}
                      className="mt-0.5 accent-primary"
                      {...userForm.register('userRole')}
                    />
                    <div>
                      <p className="font-medium text-sm">{opt.label}</p>
                      <p className="text-xs text-fg-muted mt-0.5">{opt.description}</p>
                    </div>
                  </label>
                );
              })}
              {userForm.formState.errors.userRole && (
                <p className="text-sm text-rose-500">{userForm.formState.errors.userRole.message}</p>
              )}
              <div className="flex justify-end pt-2">
                <Button type="submit">
                  Next: Review <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Step 5 — Review & submit */}
        {step === 5 && companyData && packageData && userData && (
          <Card>
            <CardTitle className="mb-4 flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              Review & confirm
            </CardTitle>

            <div className="divide-y divide-border rounded-xl border border-border overflow-hidden">
              <Section title="Company">
                <ReviewRow label="Name" value={companyData.companyName} />
                <ReviewRow label="Contact" value={`${companyData.firstName} ${companyData.lastName}`} />
                <ReviewRow label="Email" value={companyData.contactPersonEmail} />
                {companyData.contactPhoneNumber && (
                  <ReviewRow label="Phone" value={companyData.contactPhoneNumber} />
                )}
                {companyData.expectedAuthVolume && (
                  <ReviewRow label="Expected volume" value={companyData.expectedAuthVolume} />
                )}
              </Section>
              <Section title="Package">
                <ReviewRow label="Package" value={packageData.packageName} />
                <ReviewRow label="Starting balance" value={packageData.startingBalance.toLocaleString() + ' verifications'} />
                <ReviewRow label="Expiry date" value={new Date(packageData.expiryDate).toLocaleDateString()} />
              </Section>
              <Section title="Products">
                <ReviewRow
                  label="Enabled"
                  value={selectedProducts.length > 0 ? selectedProducts.join(', ') : 'None selected'}
                />
              </Section>
              <Section title="First user">
                <ReviewRow label="Email" value={companyData.contactPersonEmail} />
                <ReviewRow
                  label="Role"
                  value={ROLE_OPTIONS.find((r) => r.value === userData.userRole)?.label ?? userData.userRole}
                />
                <ReviewRow label="Password" value="Temporary — user must change on first login" />
                <ReviewRow label="API key" value="Generated by client after first sign-in" />
              </Section>
            </div>

            {serverError && (
              <div
                role="alert"
                className="mt-4 rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-700 dark:text-rose-300"
              >
                {serverError}
              </div>
            )}

            <div className="mt-6 flex items-center gap-3">
              <Button
                onClick={() => mutation.mutate()}
                isLoading={mutation.isPending}
              >
                <Key className="h-4 w-4" />
                Create client & provision access
              </Button>
              <Button type="button" variant="outline" onClick={() => setStep(4)}>
                Back
              </Button>
            </div>

            <p className="mt-3 text-xs text-fg-muted">
              Dashboard credentials are shown once after creation. The client's API key is generated separately after first login.
            </p>
          </Card>
        )}
      </div>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="bg-surface-raised px-4 py-2 text-xs font-medium text-fg-muted uppercase tracking-wide">
        {title}
      </p>
      <div className="divide-y divide-border">{children}</div>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between px-4 py-2.5 text-sm">
      <span className="text-fg-muted">{label}</span>
      <span className="font-medium text-right max-w-xs truncate">{value}</span>
    </div>
  );
}
